import { useQuery } from '@tanstack/react-query';
import req from 'utils/req';

type RawAvailableTimes = Record<string, string[]>;

export const AVAILABLE_TIMES_QUERY_KEY = (
  experienceId: string,
  date: string,
) => ['available-times', experienceId, date];

async function fetchAvailableTimes({
  experienceId,
  date,
}: {
  experienceId: string;
  date: string;
}): Promise<RawAvailableTimes> {
  if (!experienceId || !date) {
    return {};
  }
  return req(
    `${URL_API_HOST}/api/v2/agenda/experience/times/${experienceId}/${date}`,
  ).get();
}

function toTransformedFormat(raw: RawAvailableTimes) {
  const result: { time: string; locals: string[] }[] = [];

  Object.entries(raw).forEach(([hostId, times]) => {
    times.forEach((time) => {
      const existingTime = result.find((entry) => entry.time === time);
      if (existingTime) {
        existingTime.locals.push(hostId);
      } else {
        result.push({ time, locals: [hostId] });
      }
    });
  });

  result.sort((a, b) => {
    const [hourA, minuteA] = a.time.split(':').map(Number);
    const [hourB, minuteB] = b.time.split(':').map(Number);

    return hourA - hourB || minuteA - minuteB;
  });

  return result;
}

function useAvailableTimes({
  experienceId,
  date,
}: {
  experienceId: string;
  date: string | null;
}) {
  const query = useQuery({
    queryKey: AVAILABLE_TIMES_QUERY_KEY(experienceId, date ?? ''),
    queryFn: () => {
      if (!date) throw new Error('Date is required');
      return fetchAvailableTimes({ experienceId, date });
    },
    onError: (error: any) => error,
    retry: 0,
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    refetchOnMount: false,
    enabled: !!experienceId && !!date,
  });

  const rawData = query.data ?? {};

  return {
    availableTimes: toTransformedFormat(rawData),
    availableTimesRaw: rawData,
    isLoading: query.isLoading,
    isFetched: query.isFetched,
    error: query.error,
  };
}

export default useAvailableTimes;
